import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { PAGE_SIZES, calculateDimensions } from '../constants/pageSizes';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

// Storage key for localStorage (fallback only)
const STORAGE_KEY = 'storybook_selected_page_size_fallback';

// Create the context with default values
export const DimensionContext = createContext({
  pageSizes: {},
  selectedPageSize: 'us-trade',
  updatePageSize: (pageSizeKey, bookId) => {}, // Accept pageSizeKey and bookId parameters
  setCurrentBookPageSize: (bookId, pageSize) => {}, // Set page size from current book
  dimensions: { width: 0, height: 0 },
  getRawDimensions: () => ({}),
  scaleContent: () => ({}),
  currentBookId: null,
});

// Create a provider component
export const DimensionProvider = ({ children }) => {
  // Track the current book ID
  const [currentBookId, setCurrentBookId] = useState(null);
  
  // Get the book ID from URL if present
  const getBookIdFromUrl = () => {
    const urlMatch = window.location.pathname.match(/\/(edit|design|print|ship)\/([\w-]+)/);
    return urlMatch ? urlMatch[2] : null;
  };
  
  // Default to US Trade size if none is selected
  const [selectedPageSize, setSelectedPageSize] = useState(() => {
    // 1. Try to get the book ID from the URL
    const urlBookId = getBookIdFromUrl();
    
    // 2. If we have a book ID, try to get the saved page size for this specific book
    if (urlBookId) {
      const bookSpecificSize = localStorage.getItem(`book_page_size_${urlBookId}`);
      if (bookSpecificSize && PAGE_SIZES[bookSpecificSize]) {
        console.log(`Initial load - using book-specific size for ${urlBookId}:`, bookSpecificSize);
        return bookSpecificSize;
      }
    }
    
    // 3. Try session storage (current browsing session)
    const sessionPageSize = sessionStorage.getItem('current_page_size');
    if (sessionPageSize && PAGE_SIZES[sessionPageSize]) {
      console.log('Initial load - using session page size:', sessionPageSize);
      return sessionPageSize;
    }
    
    // 4. Try localStorage fallback
    const savedPageSize = localStorage.getItem(STORAGE_KEY);
    if (savedPageSize && PAGE_SIZES[savedPageSize]) {
      console.log('Initial load - using fallback saved page size:', savedPageSize);
      return savedPageSize;
    }
    
    // 5. Default to 'us-trade' if nothing else works
    return 'us-trade';
  });
  
  const [dimensions, setDimensions] = useState(() => {
    // Calculate dimensions based on the initially determined page size
    return calculateDimensions(selectedPageSize);
  });
  
  // On initial load, if we have a book ID in the URL, set it as current
  useEffect(() => {
    const urlBookId = getBookIdFromUrl();
    if (urlBookId) {
      setCurrentBookId(urlBookId);
    }
  }, []);
  
  // Function to update the page size in both state and database
  const updatePageSize = useCallback(async (pageSizeKey, bookId = null) => {
    // Ensure the page size is valid
    if (PAGE_SIZES[pageSizeKey]) {
      console.log(`DimensionContext: Updating page size to ${pageSizeKey}${bookId ? ` for book ${bookId}` : ''}`);
      
      // Update local state
      setSelectedPageSize(pageSizeKey);
      setDimensions(calculateDimensions(pageSizeKey));
      
      // Store the page size in session storage to ensure it persists during page refreshes
      // for the current browsing session
      sessionStorage.setItem('current_page_size', pageSizeKey);
      
      // Always store in localStorage as fallback
      localStorage.setItem(STORAGE_KEY, pageSizeKey);
      
      // If we have a bookId, update it in the database
      const targetBookId = bookId || currentBookId;
      if (targetBookId) {
        try {
          const bookRef = doc(db, 'books', targetBookId);
          console.log(`Updating book ${targetBookId} with new page size: ${pageSizeKey}`);
          await updateDoc(bookRef, {
            pageSize: pageSizeKey,
            updatedAt: new Date()
          });
          
          // Store book-specific page size in localStorage with book ID to persist between sessions
          localStorage.setItem(`book_page_size_${targetBookId}`, pageSizeKey);
          
          console.log(`Successfully updated page size in database for book ${targetBookId}`);
        } catch (error) {
          console.error(`Failed to update page size for book ${targetBookId}:`, error);
        }
      } else {
        console.log('No book ID available, page size only saved locally');
      }
      
      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('pageSizeChanged', { 
        detail: { pageSize: pageSizeKey, bookId: targetBookId } 
      }));
    } else {
      console.warn(`DimensionContext: Invalid page size ${pageSizeKey}`);
    }
  }, [currentBookId]);
  
  // Function to set the current book ID and load its page size
  const setCurrentBookPageSize = useCallback((bookId, pageSize) => {
    console.log(`Setting current book page size: bookId=${bookId}, pageSize=${pageSize}`);
    
    // Update current book tracking
    setCurrentBookId(bookId);
    
    // If the book has a page size, use it
    if (pageSize && PAGE_SIZES[pageSize]) {
      console.log(`Loading book-specific page size: ${pageSize}`);
      
      // Update all storage locations for consistent persistence
      localStorage.setItem(`book_page_size_${bookId}`, pageSize);
      sessionStorage.setItem('current_page_size', pageSize);
      localStorage.setItem(STORAGE_KEY, pageSize);
      
      // Update the state
      setSelectedPageSize(pageSize);
      setDimensions(calculateDimensions(pageSize));
    } else {
      console.log('Book has no page size set, using current selected size');
      
      // If we don't have a page size but we have the current selected size,
      // let's store that as the book's page size for consistency
      if (bookId && selectedPageSize) {
        console.log(`Storing current page size ${selectedPageSize} for book ${bookId}`);
        localStorage.setItem(`book_page_size_${bookId}`, selectedPageSize);
      }
    }
  }, [selectedPageSize]);

  
  // Get the raw dimensions in inches
  const getRawDimensions = () => {
    return PAGE_SIZES[selectedPageSize] || PAGE_SIZES['us-trade'];
  };
  
  // Scale content based on dimension changes
  const scaleContent = (content, originalPageSize, targetPageSize) => {
    // Get original and target dimensions
    const original = PAGE_SIZES[originalPageSize];
    const target = PAGE_SIZES[targetPageSize];
    
    if (!original || !target) return content;
    
    // Calculate scale factors
    const widthScale = target.width / original.width;
    const heightScale = target.height / original.height;
    
    // Return modified content with scaling information
    return {
      ...content,
      scalingInfo: { 
        widthScale, 
        heightScale, 
        originalSize: original, 
        targetSize: target 
      }
    };
  };
  
  return (
    <DimensionContext.Provider value={{
      pageSizes: PAGE_SIZES,
      selectedPageSize,
      updatePageSize,
      setCurrentBookPageSize,
      dimensions,
      getRawDimensions,
      scaleContent,
      currentBookId
    }}>
      {children}
    </DimensionContext.Provider>
  );
};

// Custom hook for using dimensions
export const useDimensions = () => {
  const context = useContext(DimensionContext);
  if (!context) {
    throw new Error('useDimensions must be used within a DimensionProvider');
  }
  return context;
};
